import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Line,
  Bar,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ComposedChart
} from 'recharts';
import { companyAPI } from '../services/api';
import './CompanyCharts.css';
import { usePeRatio } from '../hooks/usePeRatio';
import PeStepBar from '../components/charts/PeStepBar';


const CompanyCharts = ({ companyData, symbol }) => {
  const [timeRange, setTimeRange] = useState('1Yr');
  const [activeChart, setActiveChart] = useState('price');
  const [showMoreDropdown, setShowMoreDropdown] = useState(false);
  const [historicalData, setHistoricalData] = useState([]);
  const [loading, setLoading] = useState(false);
  
const [openInfo, setOpenInfo] = useState(null);

  // chart swap state
  const [thirdSlotChart, setThirdSlotChart] = useState('sales-margin');

  // checkbox state
  const [priceCheckboxes, setPriceCheckboxes] = useState({ price: true, volume: true });
  const [peCheckboxes, setPeCheckboxes] = useState({ eps: true, pe: true, medianPE: true });
  const [salesMarginCheckboxes, setSalesMarginCheckboxes] = useState({
  sales: true,
  opm: true,
  npm: true
});

  const [evEbitdaCheckboxes, setEvEbitdaCheckboxes] = useState({
    ebitda: true,
    evToEbitda: true,
    medianEV: true
  });
  // const [pbvCheckboxes, setPbvCheckboxes] = useState({
  //   bookValue: true,
  //   priceToBook: true,
  //   medianPBV: true
  // });
  const [mcsCheckboxes, setMcsCheckboxes] = useState({
    sales: true,
    marketCapToSales: true,
    medianMCS: true
  });

  const chartDefinitions = {
    'sales-margin': 'Sales & Margin',
    'ev-ebitda': 'EV / EBITDA',
    // 'price-to-book': 'Price to Book',
    'market-cap-sales': 'Market Cap / Sales'
  };

  
  const dropdownRef = useRef(null);

  useEffect(() => {
    const loadHistorical = async () => {
      if (!symbol) return;
      setLoading(true);
      try {
        const response = await companyAPI.getHistorical(symbol);
        let candles = response;
        if (Array.isArray(response)) {
          candles = response;
        } else if (response?.data && Array.isArray(response.data)) {
          candles = response.data;
        } else if (response?.historicals && Array.isArray(response.historicals)) {
          candles = response.historicals;
        }
        setHistoricalData(candles || []);
      } catch (err) {
        console.error('Error loading historical data', err);
        setHistoricalData([]);
      } finally {
        setLoading(false);
      }
    };

    loadHistorical();
  }, [symbol]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowMoreDropdown(false);
      }
    };
    if (showMoreDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showMoreDropdown]);


  const availableDropdownCharts = useMemo(() => {
    // const allCharts = ['sales-margin', 'ev-ebitda', 'price-to-book', 'market-cap-sales'];
    const allCharts = ['sales-margin', 'ev-ebitda','market-cap-sales'];
    return allCharts.filter((chart) => chart !== thirdSlotChart);
  }, [thirdSlotChart]);

  const handleDropdownChartSelect = (chartKey) => {
    setThirdSlotChart(chartKey);
    setActiveChart(chartKey);
    setShowMoreDropdown(false);
  };

  // Parse quarterly results for Sales & Margin chart

const quarterlyData = useMemo(() => {
  if (!companyData?.data?.quaterly_results) return [];

  const rows = companyData.data.quaterly_results;
  const headers = rows[0];

  const salesRow = rows.find(r => r[0] === 'Sales');
  const opmRow = rows.find(r => r[0] === 'OPM %');
  const netProfitRow = rows.find(r => r[0] === 'Net Profit');

  if (!salesRow || !opmRow || !netProfitRow) return [];

  return headers.slice(1).map((period, i) => {
    const sales = parseFloat(salesRow[i + 1]) || 0;
    const opm = parseFloat((opmRow[i + 1] || '0').replace('%', '')) || 0;
    const netProfit = parseFloat(netProfitRow[i + 1]) || 0;

    return {
      period,
      sales,                 
      opm,                   
      npm: sales ? +(netProfit / sales * 100).toFixed(2) : 0
    };
  });
}, [companyData]);

// ✅ Filter Sales & Margin data based on timeRange
const filteredSalesMarginData = useMemo(() => {
  if (!quarterlyData.length) return [];
  if (timeRange === '1M') return [];
  if (timeRange === '6M') return quarterlyData.slice(-2);
  if (timeRange === '1Yr') return quarterlyData.slice(-4);
  if (timeRange === '3Yr') return quarterlyData.slice(-12);
  if (timeRange === '5Yr') return quarterlyData.slice(-20);
  return quarterlyData;
}, [quarterlyData, timeRange]);

// ✅ Dynamic Y-axis start for Sales (avoid starting from zero)
const salesAxisDomain = useMemo(() => {
  if (!filteredSalesMarginData.length) return ['auto', 'auto'];

  const salesValues = filteredSalesMarginData.map(d => d.sales);
  const min = Math.min(...salesValues);
  const padding = min * 0.15; 

  return [Math.max(0, min - padding), 'auto'];
}, [filteredSalesMarginData]);

  // Parse annual data
  const annualData = useMemo(() => {
    if (!companyData?.data?.profit_and_loss) return [];
    
    const pl = companyData.data.profit_and_loss;
    if (!pl || pl.length === 0) return [];
    
    const headers = pl[0];
    const salesRow = pl.find(row => row[0] === 'Sales');
    const epsRow = pl.find(row => row[0] === 'EPS in Rs');
    const opmRow = pl.find(row => row[0] === 'OPM %');

    if (!salesRow) return [];

    const data = [];
    for (let j = 1; j < headers.length; j++) {
      if (headers[j] && headers[j].includes('Mar')) {
        const year = headers[j].replace('Mar ', '');
        const sales = salesRow[j] ? parseFloat(salesRow[j]) : 0;
        const eps = epsRow && epsRow[j] ? parseFloat(epsRow[j]) : 0;
        const opm = opmRow && opmRow[j] ? parseFloat((opmRow[j] || '0').replace('%', '')) : 0;
        
        data.push({
          year: year,
          sales: sales * 100,
          eps: eps,
          opm: opm
        });
      }
    }

    return data;
  }, [companyData]);

  // Enrich annual data with calculated metrics
  const enrichedData = useMemo(() => {
    if (!annualData.length) return [];

    const ratios = companyData?.data?.top_ratios || {};
    const currentPrice = parseFloat((ratios['Current Price'] || '0').replace(/[^\d.]/g, ''));
    const bookValue = parseFloat((ratios['Book Value'] || '0').replace(/[^\d.]/g, ''));
    const marketCapStr = (ratios['Market Cap'] || '0').replace(/[^\d.]/g, '');
    const marketCap = parseFloat(marketCapStr);
    
    return annualData.map((d,index) => {
      const pe = d.eps && d.eps > 0 ? currentPrice / d.eps : 0;
      const priceToBook = bookValue && bookValue > 0 ? currentPrice / bookValue : 0;
  

// 🔹 EBITDA = Operating Profit + Depreciation + Amortization
// 🔹 EBITDA = Operating Profit + Depreciation (Screener style)
const opProfitRow = companyData.data.profit_and_loss.find(r => r[0] === 'Operating Profit');
const depreciationRow = companyData.data.profit_and_loss.find(r => r[0] === 'Depreciation');

const operatingProfit = parseFloat(opProfitRow?.[index + 1] || 0);
const depreciation = parseFloat(depreciationRow?.[index + 1] || 0);

const ebitda = operatingProfit + depreciation;

// 🔹 EV = Market Cap + Borrowings (Cash assumed 0)
const borrowingsRow = companyData.data.balance_sheet.find(r => r[0] === 'Borrowings');
const borrowings = parseFloat(borrowingsRow?.[index + 1] || 0);

const enterpriseValue = marketCap + borrowings;

// 🔹 EV / EBITDA
const evToEbitda = ebitda > 0 ? enterpriseValue / ebitda : null;

      const marketCapToSales = d.sales && d.sales > 0 ? marketCap / d.sales : 0;

      return {
        ...d,
        pe: parseFloat(pe.toFixed(2)),
        medianPE: 34.1,
        // priceToBook: parseFloat(priceToBook.toFixed(2)),
        medianPBV: 1.8,
        bookValue: bookValue,
        ebitda: +ebitda.toFixed(2),
  evToEbitda: evToEbitda ? +evToEbitda.toFixed(2) : null,
        marketCapToSales: parseFloat(marketCapToSales.toFixed(2)),
        medianMCS: 0.7,
        
      };
    });
  }, [annualData, companyData]);
const medianEV = useMemo(() => {
  const values = enrichedData
    .map(d => d.evToEbitda)
    .filter(v => v && v > 0)
    .sort((a, b) => a - b);

  if (!values.length) return null;
  return values[Math.floor(values.length / 2)];
}, [enrichedData]);


  // Filter data based on time range
  const filteredAnnualData = useMemo(() => {
    const ranges = {
      '1M': 1/12,
      '6M': 0.5,
      '1Yr': 1,
      '3Yr': 3,
      '5Yr': 5,
      '10Yr': 10,
      'Max': enrichedData.length
    };
    
    const count = ranges[timeRange] || 5;
    return enrichedData.slice(-Math.ceil(count));
  }, [enrichedData, timeRange]);

  const filteredQuarterlyData = useMemo(() => {
    const ranges = {
      '1M': 4,
      '6M': 6,
      '1Yr': 8,
      '3Yr': 12,
      '5Yr': 20,
      '10Yr': 40,
      'Max': quarterlyData.length
    };
    
    const count = ranges[timeRange] || 20;
    return quarterlyData.slice(-Math.max(Math.ceil(count), 4));
  }, [quarterlyData, timeRange]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
     const formattedDate = new Date(label).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
    return (
      <div className="chart-tooltip">
        <p className="chart-tooltip-title">{formattedDate}</p>
        {payload.map((entry, index) => (
          <p key={index} className="chart-tooltip-item" style={{ color: entry.color }}>
            {entry.name}: {typeof entry.value === 'number' ? entry.value.toFixed(2) : entry.value}
          </p>
        ))}
      </div>
    );
  };

  const filteredPriceData = useMemo(() => {
    if (!historicalData?.length) return [];
    const now = new Date();
    let cutoff = new Date();

    switch (timeRange) {
      case '1M':
        cutoff.setMonth(now.getMonth() - 1);
        break;
      case '6M':
        cutoff.setMonth(now.getMonth() - 6);
        break;
      case '1Yr':
        cutoff.setFullYear(now.getFullYear() - 1);
        break;
      case '3Yr':
        cutoff.setFullYear(now.getFullYear() - 3);
        break;
      case '5Yr':
        cutoff.setFullYear(now.getFullYear() - 5);
        break;
      case '10Yr':
        cutoff.setFullYear(now.getFullYear() - 10);
        break;
      case 'Max':
      default:
        cutoff = null;
    }

    return historicalData
      .filter((candle) => {
        const dateStr = candle?.time || candle?.date || candle?.Date || candle?.timestamp;
        if (!dateStr) return false;
        const dt = new Date(dateStr);
        if (Number.isNaN(dt.getTime())) return false;
        return !cutoff || dt >= cutoff;
      })
      .map((candle) => ({
        date: candle?.time || candle?.date || candle?.Date || candle?.timestamp,
        price: parseFloat(candle?.close ?? candle?.price ?? candle?.Close ?? candle?.Price ?? 0),
        volume: parseFloat(candle?.volume ?? candle?.Volume ?? 0)
      }))
      .sort((a, b) => new Date(a.date) - new Date(b.date));
  }, [historicalData, timeRange]);
// 🔹 Screener-style PE data (from custom hook)
const { filteredPeSeries, ttmBars, medianPe } =
  usePeRatio(companyData, filteredPriceData, timeRange);

const roundedMedianPe = useMemo(() => {
  if (medianPe == null) return null;
  return Number(medianPe.toFixed(2));   // ✅ 2 decimal places (24.26)
}, [medianPe]);

// 🔹 Dynamic price lag for Y-axis (price padding)
const priceMinWithLag = useMemo(() => {
  if (!filteredPriceData.length) return 0;

  const prices = filteredPriceData
    .map(d => d.price)
    .filter(p => !isNaN(p));

  const minPrice = Math.min(...prices);

  const lag = minPrice * 0.02; // 40% lag (500 → 300)
  return Math.max(0, Math.floor(minPrice - lag));
}, [filteredPriceData]);

  const renderChartCheckboxes = () => {
    switch (activeChart) {
      case 'price':
        return (
          <div className="chart-checkboxes-overlay">
            <label className="checkbox-label-inline">
              <input
                type="checkbox"
                checked={priceCheckboxes.price}
                onChange={(e) => setPriceCheckboxes({ ...priceCheckboxes, price: e.target.checked })}
              />
              <span className="checkbox-indicator" style={{ backgroundColor: '#8B5CF6' }}></span>
              <span>Price</span>
            </label>
            <label className="checkbox-label-inline">
              <input
                type="checkbox"
                checked={priceCheckboxes.volume}
                onChange={(e) => setPriceCheckboxes({ ...priceCheckboxes, volume: e.target.checked })}
              />
              <span className="checkbox-indicator" style={{ backgroundColor: '#216acaff' }}></span>
              <span>Volume</span>
            </label>
          </div>
        );
      case 'pe-ratio':
        return (
          <div className="chart-checkboxes-overlay">
            <label className="checkbox-label-inline">
              <input
                type="checkbox"
                checked={peCheckboxes.eps}
                onChange={(e) => setPeCheckboxes({ ...peCheckboxes, eps: e.target.checked })}
              />
              <span className="checkbox-indicator" style={{ backgroundColor: '#93C5FD' }}></span>
              <span>TTM EPS</span>
            </label>
            <label className="checkbox-label-inline">
              <input
                type="checkbox"
                checked={peCheckboxes.pe}
                onChange={(e) => setPeCheckboxes({ ...peCheckboxes, pe: e.target.checked })}
              />
              <span className="checkbox-indicator" style={{ backgroundColor: '#8B5CF6' }}></span>
              <span>PE</span>
            </label>
            <label className="checkbox-label-inline">
              <input
                type="checkbox"
                checked={peCheckboxes.medianPE}
                onChange={(e) => setPeCheckboxes({ ...peCheckboxes, medianPE: e.target.checked })}
              />
              <span className="checkbox-indicator" style={{ backgroundColor: '#9CA3AF' }}></span>
              <span>Median PE</span>
            </label>
          </div>
        );
      case 'sales-margin':
        return (
          <div className="chart-checkboxes-overlay">
            <label className="checkbox-label-inline">
              <input
                type="checkbox"
                checked={salesMarginCheckboxes.sales}
                onChange={(e) => setSalesMarginCheckboxes({ ...salesMarginCheckboxes, sales: e.target.checked })}
              />
              <span className="checkbox-indicator" style={{ backgroundColor: '#A78BFA' }}></span>
              <span>Sales</span>
            </label>
            
            <label className="checkbox-label-inline">
              <input
                type="checkbox"
                checked={salesMarginCheckboxes.opm}
                onChange={(e) => setSalesMarginCheckboxes({ ...salesMarginCheckboxes, opm: e.target.checked })}
              />
              <span className="checkbox-indicator" style={{ backgroundColor: '#EAB308' }}></span>
              <span>OPM %</span>
            </label>
            <label className="checkbox-label-inline">
              <input
                type="checkbox"
                checked={salesMarginCheckboxes.npm}
                onChange={(e) => setSalesMarginCheckboxes({ ...salesMarginCheckboxes, npm: e.target.checked })}
              />
              <span className="checkbox-indicator" style={{ backgroundColor: '#10B981' }}></span>
              <span>NPM %</span>
            </label>
          </div>
        );
      case 'ev-ebitda':
        return (
          <div className="chart-checkboxes-overlay">
            <label className="checkbox-label-inline">
              <input
                type="checkbox"
                checked={evEbitdaCheckboxes.ebitda}
                onChange={(e) => setEvEbitdaCheckboxes({ ...evEbitdaCheckboxes, ebitda: e.target.checked })}
              />
              <span className="checkbox-indicator" style={{ backgroundColor: '#60A5FA' }}></span>
              <span>EBITDA</span>
            </label>
            <label className="checkbox-label-inline">
              <input
                type="checkbox"
                checked={evEbitdaCheckboxes.evToEbitda}
                onChange={(e) => setEvEbitdaCheckboxes({ ...evEbitdaCheckboxes, evToEbitda: e.target.checked })}
              />
              <span className="checkbox-indicator" style={{ backgroundColor: '#8B5CF6' }}></span>
              <span>EV / EBITDA</span>
            </label>
            <label className="checkbox-label-inline">
              <input
                type="checkbox"
                checked={evEbitdaCheckboxes.medianEV}
                onChange={(e) => setEvEbitdaCheckboxes({ ...evEbitdaCheckboxes, medianEV: e.target.checked })}
              />
              <span className="checkbox-indicator" style={{ backgroundColor: '#9CA3AF' }}></span>
              <span>Median EV</span>
            </label>
          </div>
        );
      // case 'price-to-book':
      //   return (
      //     <div className="chart-checkboxes-overlay">
      //       <label className="checkbox-label-inline">
      //         <input
      //           type="checkbox"
      //           checked={pbvCheckboxes.bookValue}
      //           onChange={(e) => setPbvCheckboxes({ ...pbvCheckboxes, bookValue: e.target.checked })}
      //         />
      //         <span className="checkbox-indicator" style={{ backgroundColor: '#93C5FD' }}></span>
      //         <span>Book Value</span>
      //       </label>
      //       <label className="checkbox-label-inline">
      //         <input
      //           type="checkbox"
      //           checked={pbvCheckboxes.priceToBook}
      //           onChange={(e) => setPbvCheckboxes({ ...pbvCheckboxes, priceToBook: e.target.checked })}
      //         />
      //         <span className="checkbox-indicator" style={{ backgroundColor: '#8B5CF6' }}></span>
      //         <span>Price to BV</span>
      //       </label>
      //       <label className="checkbox-label-inline">
      //         <input
      //           type="checkbox"
      //           checked={pbvCheckboxes.medianPBV}
      //           onChange={(e) => setPbvCheckboxes({ ...pbvCheckboxes, medianPBV: e.target.checked })}
      //         />
      //         <span className="checkbox-indicator" style={{ backgroundColor: '#9CA3AF' }}></span>
      //         <span>Median PBV</span>
      //       </label>
      //     </div>
      //   );
      case 'market-cap-sales':
        return (
          <div className="chart-checkboxes-overlay">
            <label className="checkbox-label-inline">
              <input
                type="checkbox"
                checked={mcsCheckboxes.sales}
                onChange={(e) => setMcsCheckboxes({ ...mcsCheckboxes, sales: e.target.checked })}
              />
              <span className="checkbox-indicator" style={{ backgroundColor: '#93C5FD' }}></span>
              <span>Sales</span>
            </label>
            <label className="checkbox-label-inline">
              <input
                type="checkbox"
                checked={mcsCheckboxes.marketCapToSales}
                onChange={(e) => setMcsCheckboxes({ ...mcsCheckboxes, marketCapToSales: e.target.checked })}
              />
              <span className="checkbox-indicator" style={{ backgroundColor: '#8B5CF6' }}></span>
              <span>Market Cap / Sales</span>
            </label>
            <label className="checkbox-label-inline">
              <input
                type="checkbox"
                checked={mcsCheckboxes.medianMCS}
                onChange={(e) => setMcsCheckboxes({ ...mcsCheckboxes, medianMCS: e.target.checked })}
              />
              <span className="checkbox-indicator" style={{ backgroundColor: '#9CA3AF' }}></span>
              <span>Median MC/S</span>
            </label>
          </div>
        );
      default:
        return null;
    }
  };

  const renderChart = () => {
    switch(activeChart) {
      case 'price': {
        if (loading) {
          return (
            <div className="chart-empty">
              <p>Loading price data...</p>
            </div>
          );
        }

        if (!filteredPriceData.length) {
          return (
            <div className="chart-empty">
              <p>No price data available</p>
            </div>
          );
        }

        return (
          <>
          {/* <div className="chart-disclaimer">
  Price reflects the market price of the stock over the selected time period,
  based on available historical pricing data.
</div> */}
<div className="pe-disclaimer">
  <div className="info-header">
    <span
      className="info-icon"
      onClick={() =>
        setOpenInfo(openInfo === 'price' ? null : 'price')
      }
    >ⓘ</span>
      <span className="info-title">How Price is shown</span>
</div>
{openInfo === 'price' && (
    <p className="info-text">
      Price chart displays historical daily closing prices of the stock over the
      selected time period. Prices are based on available market data and do not
      reflect intraday movements. Data may not account for corporate actions such
      as bonuses, splits, or dividends unless explicitly adjusted.
    </p>
  )}
</div>


          <ResponsiveContainer width="100%" height={400}>
  <ComposedChart data={filteredPriceData}>

    {/* ⭐ NEW: Gradient definition */}
    <defs>
      <linearGradient id="priceGradient" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.35} />
        <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0.05} />
      </linearGradient>
    </defs>

    {/* ⭐ UPDATED: cleaner grid */}
    <CartesianGrid
      strokeDasharray="3 3"
      vertical={false}
      stroke="#e5e7eb"
    />

    <XAxis
      dataKey="date"
      tick={{ fontSize: 12 }}
      padding={{ left: 10, right: 10 }}
      interval="preserveStartEnd"
      minTickGap={50}
      tickFormatter={(value) =>
  new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: timeRange === '1M' ? undefined : '2-digit'
  })
}

    />

    <YAxis
      yAxisId="left"
      tick={{ fontSize: 12 }}
      domain={[priceMinWithLag, 'auto']}
      label={{ value: 'Price', angle: -90, position: 'insideLeft', fontSize: 11 }}
    />

    <YAxis
      yAxisId="right"
      orientation="right"
      tick={{ fontSize: 12 }}
      scale="sqrt"
      domain={['auto', 'auto']}
      label={{
        value: 'Volume',
        angle: 90,
        position: 'insideRight',
        fontSize: 11
      }}
    />

    {/* ⭐ UPDATED: tooltip + vertical dashed hover line */}
    <Tooltip
      content={<CustomTooltip />}
      cursor={{ stroke: "#9ca3af", strokeDasharray: "4 4" }}
    labelFormatter={(value) =>
  new Date(value).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  })
}

    />

    {/* Volume bars (UNCHANGED) */}
    {priceCheckboxes.volume && (
      <Bar
        yAxisId="right"
        dataKey="volume"
        fill="#B4D4FF"
        fillOpacity={0.8}
        barSize={8}
        radius={[4, 4, 0, 0]}
        name="Volume"
      />
    )}

    {/* ⭐ NEW: Area under price (gradient) */}
    {priceCheckboxes.price && (
      <Area
        yAxisId="left"
        type="monotone"
        dataKey="price"
        stroke="#8B5CF6"
        strokeWidth={2}
        fill="url(#priceGradient)"
        dot={false}
        activeDot={{
          r: 5,
          fill: "#8B5CF6",
          stroke: "#ffffff",
          strokeWidth: 2
        }}
        name="Price"
      />
    )}

  </ComposedChart>
</ResponsiveContainer>

          </>
        );
      }

   case 'pe-ratio': {
  return (
    <>  
{/* // <div className="chart-disclaimer">
//   P/E Ratio is calculated using the current market price relative to reported
//   earnings, shown across historical periods for valuation comparison.
// </div> */}
<div className="pe-disclaimer">
  <div className="info-header">
    <span
      className="info-icon"
      onClick={() => setOpenInfo(openInfo === 'pe' ? null : 'pe')}
    >
      ⓘ
    </span>
    <span className="info-title">How P/E is calculated</span>
  </div>

  {openInfo === 'pe' && (
    <p className="info-text">
      P/E Ratio is calculated using daily closing prices divided by trailing
      twelve-month (TTM) EPS. TTM EPS is derived as the rolling sum of the most
      recent four reported quarterly EPS values. Median P/E represents the
      median valuation observed during the selected time range. Calculations
      are based on reported data and may not reflect restatements or
      extraordinary items.
    </p>
  )}
</div>

    <ResponsiveContainer width="100%" height={400}>
      <ComposedChart
        key={activeChart} // 🔥 IMPORTANT: forces full remount
        data={filteredPeSeries}
        margin={{ top: 5, right: 30, left: 10, bottom: 5 }}
      >
        <defs>
          <linearGradient id="peChartAreaGradient" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.15} />
            <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0.01} />
          </linearGradient>
        </defs>

        <CartesianGrid
          strokeDasharray="3 3"
          stroke="#e5e7eb"
          vertical={false}
        />

        {/* 🔴 FIXED X-AXIS (NO math inside strings) */}
        <XAxis
          dataKey="date"
          type="number"
          scale="time"
          domain={['dataMin', 'dataMax']}
          padding={{ left: 10, right: 10 }}
          tick={{ fontSize: 11, fill: '#6b7280' }}
          tickLine={false}
          axisLine={{ stroke: '#e5e7eb' }}
          tickFormatter={(v) => {
            const date = new Date(v);
            if (timeRange === '1M' || timeRange === '6M') {
              return date.toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric'
              });
            }
            return date.toLocaleDateString('en-US', {
              month: 'short',
              year: '2-digit'
            });
          }}
        />

        {/* LEFT AXIS — TTM EPS */}
        <YAxis
          yAxisId="left"
          domain={[0, 'auto']}
          tick={{ fontSize: 11, fill: '#6b7280' }}
          tickLine={false}
          axisLine={{ stroke: '#e5e7eb' }}
          label={{
            value: 'TTM EPS (₹)',
            angle: -90,
            position: 'insideLeft',
            style: { fontSize: 11, fill: '#6b7280' }
          }}
        />

        {/* RIGHT AXIS — PE */}
        <YAxis
          yAxisId="right"
          orientation="right"
          domain={['auto', 'auto']}
          tick={{ fontSize: 11, fill: '#6b7280' }}
          tickLine={false}
          axisLine={{ stroke: '#e5e7eb' }}
          label={{
            value: 'P/E Ratio',
            angle: 90,
            position: 'insideRight',
            style: { fontSize: 11, fill: '#6b7280' }
          }}
        />

        <Tooltip
  cursor={{ stroke: "#9ca3af", strokeDasharray: "4 4" }}   // ⭐ ADD
  contentStyle={{
    backgroundColor: 'rgba(255, 255, 255, 0.96)',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    padding: '12px'
  }}
  formatter={(value, name) => {
    if (name === 'ttmEps') return [`₹${value.toFixed(2)}`, 'TTM EPS'];
    if (name === 'pe') return [value.toFixed(2), 'P/E Ratio'];
    if (name === 'Median PE') return [value.toFixed(2), 'Median P/E'];
    return value;
  }}
  labelFormatter={(label) =>
    new Date(label).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }
/>
        {/* ✅ EPS STEP BARS (PE ONLY) */}
        {peCheckboxes.eps && ttmBars?.length > 0 && (
          <Bar
            yAxisId="left"
            data={ttmBars}
            dataKey="ttmEps"
            shape={PeStepBar}   // ✅ correct usage
            isAnimationActive={false}
            name="TTM EPS"
          />
        )}

        {/* PE LINE */}
        {peCheckboxes.pe && (
  <Area
    yAxisId="right"
    type="monotone"
    dataKey="pe"
    stroke="#8B5CF6"
    strokeWidth={2.5}
    fill="url(#peChartAreaGradient)"
    dot={false}
    activeDot={{
      r: 5,
      fill: "#8B5CF6",
      stroke: "#ffffff",
      strokeWidth: 2
    }}
    name="P/E"
  />
)}


        {/* MEDIAN PE */}
        {peCheckboxes.medianPE && roundedMedianPe && (
          <Line
            yAxisId="right"
            type="monotone"
            dataKey={() => roundedMedianPe}
            stroke="#9CA3AF"
            strokeWidth={1.5}
            strokeDasharray="6 4"
            dot={false}
            name="Median P/E"
          />
        )}

        <Legend
          verticalAlign="top"
          height={36}
          iconType="line"
          wrapperStyle={{ paddingBottom: '10px', fontSize: '12px' }}
        />
      </ComposedChart>
    </ResponsiveContainer>
    </>
  );
}

    case 'sales-margin':
  return (
    <>
    {/* <div className="chart-disclaimer">
  Sales represents reported revenue, while margin percentages are derived from
  disclosed profitability figures over the selected period.
</div> */}
<div className="pe-disclaimer">
   <div className="info-header">
    <span
      className="info-icon"
      onClick={() => setOpenInfo(openInfo === 'sales' ? null : 'sales')}
    >
      ⓘ
    </span>
    <span className="info-title">How Sales & Margin are shown</span>
  </div>

  {openInfo === 'sales' && (
   <p className="info-text">
      Sales values represent reported quarterly revenue figures. Operating
      Profit Margin (OPM %) is shown as disclosed in quarterly results. Net
      Profit Margin (NPM %) is calculated as Net Profit divided by Sales for
      each quarter. Data is displayed on a quarterly basis and filtered
      according to the selected time range, without adjustments for
      exceptional items or restatements.
    </p>
 )}
</div>

    <ResponsiveContainer width="100%" height={400}>
      <ComposedChart
        key={activeChart}                    // 🔥 prevent chart leakage
        data={filteredSalesMarginData}       // ✅ filtered data
        margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
      >
        <defs>
  <linearGradient id="opmGradient" x1="0" y1="0" x2="0" y2="1">
    <stop offset="0%" stopColor="#EAB308" stopOpacity={0.15} />
    <stop offset="100%" stopColor="#EAB308" stopOpacity={0.02} />
  </linearGradient>

  <linearGradient id="npmGradient" x1="0" y1="0" x2="0" y2="1">
    <stop offset="0%" stopColor="#10B981" stopOpacity={0.15} />
    <stop offset="100%" stopColor="#10B981" stopOpacity={0.02} />
  </linearGradient>
</defs>

        <CartesianGrid
  strokeDasharray="3 3"
  vertical={false}
  stroke="#e5e7eb"
/>

        <XAxis
          dataKey="period"
          tick={{ fontSize: 12 }}
          tickLine={false}
          axisLine={{ stroke: '#E5E7EB' }}
          padding={{ left: 10, right: 10 }}
        />

        {/* LEFT AXIS — SALES (₹ Cr) */}
        <YAxis
          yAxisId="left"
          domain={salesAxisDomain}            // ✅ starts below lowest value
          tick={{ fontSize: 12 }}
          tickLine={false}
          axisLine={{ stroke: '#E5E7EB' }}
          label={{
            value: 'Sales (₹ Cr)',
            angle: -90,
            position: 'insideLeft',
            fontSize: 11
          }}
        />

        {/* RIGHT AXIS — MARGINS (%) */}
        <YAxis
          yAxisId="right"
          orientation="right"
          domain={['auto', 'auto']}
          tick={{ fontSize: 12 }}
          tickLine={false}
          axisLine={{ stroke: '#E5E7EB' }}
          label={{
            value: 'Margin (%)',
            angle: 90,
            position: 'insideRight',
            fontSize: 11
          }}
        />

        <Tooltip
  cursor={{ stroke: "#9ca3af", strokeDasharray: "4 4" }}
  formatter={(value, name) => {
    if (name === 'Quarter Sales') return [`₹${value} Cr`, 'Sales'];
    if (name === 'OPM %') return [`${value}%`, 'OPM'];
    if (name === 'NPM %') return [`${value}%`, 'NPM'];
    return value;
  }}
/>

      {salesMarginCheckboxes.sales && (
  <Bar
    yAxisId="left"
    dataKey="sales"
    fill="#A78BFA"
    name="Quarter Sales"
    barSize={32}
    radius={[6, 6, 0, 0]}
  />
)}


        {salesMarginCheckboxes.opm && (
  <Area
    yAxisId="right"
    type="monotone"
    dataKey="opm"
    stroke="#EAB308"
    strokeWidth={2}
    fill="url(#opmGradient)"
    dot={false}
    activeDot={{
      r: 4,
      fill: "#EAB308",
      stroke: "#ffffff",
      strokeWidth: 2
    }}
    name="OPM %"
  />
)}


       {salesMarginCheckboxes.npm && (
  <Area
    yAxisId="right"
    type="monotone"
    dataKey="npm"
    stroke="#10B981"
    strokeWidth={2}
    fill="url(#npmGradient)"
    dot={false}
    activeDot={{
      r: 4,
      fill: "#10B981",
      stroke: "#ffffff",
      strokeWidth: 2
    }}
    name="NPM %"
  />
)}

      </ComposedChart>
    </ResponsiveContainer>
    </>
  );

case 'ev-ebitda':
  return (
    <>
    <div className="pe-disclaimer">
   <div className="info-header">
    <span
      className="info-icon"
      onClick={() => setOpenInfo(openInfo === 'ev' ? null : 'ev')}
    >
      ⓘ
    </span>
    <span className="info-title">How EV / EBITDA is calculated</span>
  </div>

  {openInfo === 'ev' && (
     <p className="info-text">
      EBITDA is calculated as the sum of Operating Profit and Depreciation
      based on annual financial statements. Enterprise Value (EV) is derived
      as Market Capitalization plus total Borrowings, with cash and cash
      equivalents assumed to be zero. EV / EBITDA is computed by dividing EV
      by EBITDA and displayed for each financial year. Median EV / EBITDA
      represents the median of calculated values over the selected time
      range. Calculations are based on reported data and do not adjust for
      extraordinary items or restatements.
    </p>
  )}
</div>

      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart
          data={filteredAnnualData}
          margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
        >

          {/* ⭐ Gradient for EV / EBITDA */}
          <defs>
            <linearGradient id="evEbitdaGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.18} />
              <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0.03} />
            </linearGradient>
          </defs>

          {/* ⭐ Clean grid */}
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="#e5e7eb"
          />

          <XAxis
            dataKey="year"
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={{ stroke: '#E5E7EB' }}
            padding={{ left: 10, right: 10 }}
          />

          {/* LEFT: EBITDA */}
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={{ stroke: '#E5E7EB' }}
            domain={[
              (dataMin) => Math.max(0, dataMin * 0.85),
              'auto'
            ]}
            label={{
              value: 'EBITDA',
              angle: -90,
              position: 'insideLeft',
              fontSize: 11
            }}
          />

          {/* RIGHT: EV / EBITDA */}
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={{ stroke: '#E5E7EB' }}
            domain={[
              (dataMin) => Math.max(0, dataMin * 0.85),
              'auto'
            ]}
            label={{
              value: 'EV / EBITDA',
              angle: 90,
              position: 'insideRight',
              fontSize: 11
            }}
          />

          {/* ⭐ Tooltip + vertical hover line */}
          <Tooltip
            cursor={{ stroke: "#9ca3af", strokeDasharray: "4 4" }}
            content={<CustomTooltip />}
          />

          {/* EBITDA bars (unchanged) */}
          {evEbitdaCheckboxes.ebitda && (
            <Bar
              yAxisId="left"
              dataKey="ebitda"
              fill="#93C5FD"
              name="EBITDA"
              barSize={32}
              radius={[6, 6, 0, 0]}
            />
          )}

          {/* ⭐ EV / EBITDA — Area + line */}
          {evEbitdaCheckboxes.evToEbitda && (
            <Area
              yAxisId="right"
              type="monotone"
              dataKey="evToEbitda"
              stroke="#8B5CF6"
              strokeWidth={2.5}
              fill="url(#evEbitdaGradient)"
              dot={false}
              activeDot={{
                r: 5,
                fill: "#8B5CF6",
                stroke: "#ffffff",
                strokeWidth: 2
              }}
              name="EV / EBITDA"
            />
          )}

          {/* Median EV / EBITDA (reference only) */}
          {evEbitdaCheckboxes.medianEV && (
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="medianEV"
              stroke="#9CA3AF"
              strokeWidth={1.5}
              strokeDasharray="5 5"
              dot={false}
              name="Median EV / EBITDA"
            />
          )}

        </ComposedChart>
      </ResponsiveContainer>
    </>
  );

  //     case 'price-to-book':
  // return (
  //   <>
  //     <ResponsiveContainer width="100%" height={400}>
  //       <ComposedChart
  //         data={filteredAnnualData}
  //         margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
  //       >

  //         {/* ⭐ Gradient for PBV */}
  //         <defs>
  //           <linearGradient id="pbvGradient" x1="0" y1="0" x2="0" y2="1">
  //             <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.18} />
  //             <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0.03} />
  //           </linearGradient>
  //         </defs>

  //         {/* ⭐ Clean grid */}
  //         <CartesianGrid
  //           strokeDasharray="3 3"
  //           vertical={false}
  //           stroke="#e5e7eb"
  //         />

  //         <XAxis
  //           dataKey="year"
  //           tick={{ fontSize: 12 }}
  //           tickLine={false}
  //           axisLine={{ stroke: '#E5E7EB' }}
  //           padding={{ left: 10, right: 10 }}
  //         />

  //         {/* LEFT — BOOK VALUE */}
  //         <YAxis
  //           yAxisId="left"
  //           tick={{ fontSize: 12 }}
  //           tickLine={false}
  //           axisLine={{ stroke: '#E5E7EB' }}
  //           label={{
  //             value: 'Book Value',
  //             angle: -90,
  //             position: 'insideLeft',
  //             fontSize: 11
  //           }}
  //         />

  //         {/* RIGHT — PRICE / BOOK */}
  //         <YAxis
  //           yAxisId="right"
  //           orientation="right"
  //           tick={{ fontSize: 12 }}
  //           tickLine={false}
  //           axisLine={{ stroke: '#E5E7EB' }}
  //           label={{
  //             value: 'Price / Book',
  //             angle: 90,
  //             position: 'insideRight',
  //             fontSize: 11
  //           }}
  //         />

  //         {/* ⭐ Tooltip + vertical hover line */}
  //         <Tooltip
  //           cursor={{ stroke: "#9ca3af", strokeDasharray: "4 4" }}
  //           content={<CustomTooltip />}
  //         />

  //         {/* BOOK VALUE BARS (unchanged) */}
  //         {pbvCheckboxes.bookValue && (
  //           <Bar
  //             yAxisId="left"
  //             dataKey="bookValue"
  //             fill="#93C5FD"
  //             name="Book Value"
  //             barSize={32}
  //             radius={[6, 6, 0, 0]}
  //           />
  //         )}

  //         {/* ⭐ PRICE / BOOK — Area + line */}
  //         {pbvCheckboxes.priceToBook && (
  //           <Area
  //             yAxisId="right"
  //             type="monotone"
  //             dataKey="priceToBook"
  //             stroke="#8B5CF6"
  //             strokeWidth={2.5}
  //             fill="url(#pbvGradient)"
  //             dot={false}
  //             activeDot={{
  //               r: 5,
  //               fill: "#8B5CF6",
  //               stroke: "#ffffff",
  //               strokeWidth: 2
  //             }}
  //             name="Price / Book"
  //           />
  //         )}

  //         {/* MEDIAN PBV — dashed reference */}
  //         {pbvCheckboxes.medianPBV && (
  //           <Line
  //             yAxisId="right"
  //             type="monotone"
  //             dataKey="medianPBV"
  //             stroke="#9CA3AF"
  //             strokeWidth={1.5}
  //             strokeDasharray="5 5"
  //             dot={false}
  //             name="Median PBV"
  //           />
  //         )}

  //       </ComposedChart>
  //     </ResponsiveContainer>
  //   </>
  // );


     case 'market-cap-sales':
  return (
    <>
    <div className="pe-disclaimer">
    <div className="info-header">
    <span
      className="info-icon"
      onClick={() => setOpenInfo(openInfo === 'mcs' ? null : 'mcs')}
    >
      ⓘ
    </span>
    <span className="info-title">How MC / Sales is calculated</span>
  </div>

  {openInfo === 'mcs' && (
   <p className="info-text">
        Sales represent reported annual revenue values, while Market Cap to Sales
        is calculated using the latest reported Market Capitalization divided by
        annual Sales for each year.
      </p>
  )}
</div>

      <ResponsiveContainer width="100%" height={400}>
        <ComposedChart
          data={filteredAnnualData}
          margin={{ top: 20, right: 30, left: 0, bottom: 20 }}
        >

          {/* ⭐ Gradient for MC/S */}
          <defs>
            <linearGradient id="mcsGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.18} />
              <stop offset="100%" stopColor="#8B5CF6" stopOpacity={0.03} />
            </linearGradient>
          </defs>

          {/* ⭐ Clean grid */}
          <CartesianGrid
            strokeDasharray="3 3"
            vertical={false}
            stroke="#e5e7eb"
          />

          <XAxis
            dataKey="year"
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={{ stroke: '#E5E7EB' }}
            padding={{ left: 10, right: 10 }}
          />

          {/* LEFT — SALES */}
          <YAxis
            yAxisId="left"
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={{ stroke: '#E5E7EB' }}
            label={{
              value: 'Sales',
              angle: -90,
              position: 'insideLeft',
              fontSize: 11
            }}
          />

          {/* RIGHT — MC / SALES */}
          <YAxis
            yAxisId="right"
            orientation="right"
            tick={{ fontSize: 12 }}
            tickLine={false}
            axisLine={{ stroke: '#E5E7EB' }}
            label={{
              value: 'MC / Sales',
              angle: 90,
              position: 'insideRight',
              fontSize: 11
            }}
          />

          {/* ⭐ Tooltip + vertical hover line */}
          <Tooltip
            cursor={{ stroke: "#9ca3af", strokeDasharray: "4 4" }}
            content={<CustomTooltip />}
          />

          {/* SALES BARS (unchanged) */}
          {mcsCheckboxes.sales && (
            <Bar
              yAxisId="left"
              dataKey="sales"
              fill="#93C5FD"
              name="Sales"
              barSize={32}
              radius={[6, 6, 0, 0]}
            />
          )}

          {/* ⭐ MC / SALES — Area + line */}
          {mcsCheckboxes.marketCapToSales && (
            <Area
              yAxisId="right"
              type="monotone"
              dataKey="marketCapToSales"
              stroke="#8B5CF6"
              strokeWidth={2.5}
              fill="url(#mcsGradient)"
              dot={false}
              activeDot={{
                r: 5,
                fill: "#8B5CF6",
                stroke: "#ffffff",
                strokeWidth: 2
              }}
              name="Market Cap / Sales"
            />
          )}

          {/* MEDIAN MC/S — dashed line only */}
          {mcsCheckboxes.medianMCS && (
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="medianMCS"
              stroke="#9CA3AF"
              strokeWidth={1.5}
              strokeDasharray="5 5"
              dot={false}
              name="Median MC/S"
            />
          )}

        </ComposedChart>
      </ResponsiveContainer>
    </>
  );


      default:
        return null;
    }
  };

  const timeRanges = ['1M', '6M', '1Yr', '3Yr', '5Yr', '10Yr', 'Max'];

  return (
    <div className="company-charts">
      <div className="charts-controls">
        <div className="time-range-selector">
          {timeRanges.map((range) => (
            <button
              key={range}
              onClick={() => setTimeRange(range)}
              className={`time-btn ${timeRange === range ? 'active' : ''}`}
            >
              {range}
            </button>
          ))}
        </div>

        <div className="chart-type-selector">
          <button
            className={`chart-btn ${activeChart === 'price' ? 'active' : ''}`}
            onClick={() => setActiveChart('price')}
          >
            Price
          </button>
          <button
            className={`chart-btn ${activeChart === 'pe-ratio' ? 'active' : ''}`}
            onClick={() => setActiveChart('pe-ratio')}
          >
            PE Ratio
          </button>
          <button
            className={`chart-btn ${activeChart === thirdSlotChart ? 'active' : ''}`}
            onClick={() => setActiveChart(thirdSlotChart)}
          >
            {chartDefinitions[thirdSlotChart]}
          </button>

          <div className="more-dropdown" ref={dropdownRef}>
            <button className="chart-btn more-btn" onClick={() => setShowMoreDropdown((prev) => !prev)}>
              More ▼
            </button>

            {showMoreDropdown && (
              <div className="dropdown-menu">
                {availableDropdownCharts.map((chartKey) => (
                  <button
                    key={chartKey}
                    className="dropdown-item"
                    onClick={() => handleDropdownChartSelect(chartKey)}
                  >
                    {chartDefinitions[chartKey]}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

     <div className="chart-container">
  <div className="chart-scroll-wrapper">
    <div className="chart-scroll-inner">
      {renderChart()}
    </div>
  </div>

  {renderChartCheckboxes()}
</div>


    </div>
  );
};

export default CompanyCharts;